import { useState } from "react";
import { PATHS } from "../../data/site";
import { isOffsite } from "../../lib/img";
import { Eyebrow, Photo, Reveal, SmartLink } from "./primitives";
import { ArrowUpRight, Plus } from "lucide-react";

const PathLink = ({ l, cardId }) => (
    <SmartLink
      href={l.href}
      data-testid={`path-link-${cardId}-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`group/l inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-5 text-sm font-semibold transition-colors duration-300 ${
        l.primary ? "bg-ivory text-ink hover:bg-white" : "border border-ivory/40 text-ivory hover:bg-ivory/10"
      }`}
    >
      {l.label}
      <ArrowUpRight aria-hidden className="h-4 w-4 transition-transform group-hover/l:-translate-y-0.5 group-hover/l:translate-x-0.5" />
      {isOffsite(l.href) && <span className="sr-only">(opens in a new tab)</span>}
    </SmartLink>
);

const PathCard = ({ p, active, onActivate }) => (
  <article
    data-testid={`path-card-${p.id}`}
    data-active={active}
    onMouseEnter={onActivate}
    onFocus={onActivate}
    onClick={onActivate}
    className={`relative isolate flex min-h-[460px] overflow-hidden rounded-[1.75rem] bg-ink text-ivory transition-[flex-grow] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] lg:min-h-0 lg:basis-0 ${active ? "lg:grow-[2.3]" : "lg:grow"}`}
  >
    <Photo img={p.img} sizes="(min-width:1024px) 55vw, 100vw" style={p.posClass ? { objectPosition: undefined } : undefined} className={`absolute inset-0 -z-10 h-full w-full object-cover ${p.posClass || ""} transition-[transform,filter] duration-[1400ms] ease-out ${active ? "scale-105 saturate-100" : "scale-100 saturate-[0.85]"}`} />
    <div aria-hidden className={`absolute inset-0 -z-10 transition-opacity duration-700 bg-[linear-gradient(to_top,rgba(24,21,18,0.94)_10%,rgba(24,21,18,0.8)_55%,rgba(24,21,18,0.62))] ${active ? "opacity-100" : "opacity-90"}`} />
    <div className="flex w-full flex-col justify-end p-7 sm:p-9">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tabular-nums tracking-[0.3em] text-ivory [text-shadow:0_1px_8px_rgba(20,16,12,0.6)]">{p.n}</span>
      </div>
      <h3 className="mt-3">
        <button
          type="button"
          onClick={onActivate}
          aria-expanded={active}
          aria-controls={`${p.id}-details`}
          data-testid={`path-toggle-${p.id}`}
          className="flex min-h-[44px] w-full items-center justify-between gap-4 text-left font-display text-[2.1rem] font-normal leading-tight sm:text-4xl rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ivory"
        >
          {p.title}
          <Plus aria-hidden className={`h-6 w-6 shrink-0 transition-transform duration-500 ${active ? "rotate-45" : ""}`} strokeWidth={1.25} />
        </button>
      </h3>
      <p className={`mt-3 max-w-lg text-[15px] leading-relaxed text-ivory/85 ${active ? "" : "lg:line-clamp-3"}`}>{p.summary}</p>
      <div
        id={`${p.id}-details`}
        inert={!active}
        data-testid={`path-details-${p.id}`}
        className={`grid transition-[grid-template-rows,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${active ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <ul className="mt-6 flex flex-wrap gap-2" aria-label={`${p.title} includes`}>
            {p.offerings.map((o) => (
              <li key={o} className="rounded-full border border-ivory/25 bg-ivory/10 px-3.5 py-1.5 text-xs font-medium tracking-wide text-ivory backdrop-blur-sm">
                {o}
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-wrap gap-2.5 pb-1">
            {p.links.map((l) => (
              <PathLink key={l.label} l={l} cardId={p.id} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </article>
);

export default function Paths() {
  const [active, setActive] = useState(0);
  return (
    <section id="paths" aria-labelledby="paths-title" data-testid="paths-section" className="scroll-mt-24 bg-sand-light px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 px-2 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Eyebrow>Choose your path</Eyebrow>
            <h2 id="paths-title" className="mt-5 max-w-2xl font-display text-[2.4rem] font-light leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
              Find the entry point that feels right for you.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-sm leading-relaxed text-ink-soft">
              Select a path to see what it includes.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 flex flex-col gap-4 lg:h-[660px] lg:flex-row">
          {PATHS.map((p, i) => (
            <PathCard key={p.id} p={p} active={active === i} onActivate={() => setActive(i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
